import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { DiaryEntrySubmissionSummaryResponse } from "@/types/person"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface DiaryEntrySubmissionSummaryRequest {
  startDate: string
  endDate: string
}

export function useDiaryEntrySubmissionSummary(request: DiaryEntrySubmissionSummaryRequest) {
  return useQuery<DiaryEntrySubmissionSummaryResponse>({
    queryKey: ["diary-entry-submission-summary", request],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        throw new Error("No authentication token available")
      }

      const functionUrl = `${getFunctionsBaseUrl()}/diary-entry-submission-summary`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.error ||
            `Failed to fetch diary entry submission summary: ${response.statusText}`,
        )
      }

      const data: DiaryEntrySubmissionSummaryResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch diary entry submission summary")
      }

      return data
    },
    enabled: !!request.startDate && !!request.endDate,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })
}
